import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  ListMasterTemplateUserResultsResponse,
  MasterTemplateReport,
} from '@/client/campaigns/types/master-template-report.type';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';

export const masterTemplateReportQueryKey = 'masterTemplateReport';
export const masterTemplateUserResultsQueryKey = 'masterTemplateUserResults';

export const useMasterTemplateReport = (
  accountId?: string,
  masterTemplateId?: string,
  params?: UseQueryOptions & { filters?: FiltersType },
): UseQueryResult<MasterTemplateReport> & {
  masterTemplateReport: MasterTemplateReport | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    filters,
    enabled = true,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [masterTemplateReportQueryKey, accountId, masterTemplateId, filters],
    () =>
      client.masterTemplateReports.getReport(
        accountId as string,
        masterTemplateId as string,
        { filters },
      ),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<MasterTemplateReport>),
      enabled: !!accountId && !!masterTemplateId && !!enabled,
    },
  );

  return {
    masterTemplateReport: data,
    ...(rest as UseQueryResult<MasterTemplateReport>),
  };
};

export const useMasterTemplateUserResults = (
  accountId?: string,
  masterTemplateId?: string,
  params?: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
): UseQueryResult<ListMasterTemplateUserResultsResponse> & {
  masterTemplateUserResults: ListMasterTemplateUserResultsResponse | undefined;
} => {
  const {
    take = 10,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    enabled = true,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [
      masterTemplateUserResultsQueryKey,
      accountId,
      masterTemplateId,
      take,
      skip,
      sort,
      filters,
    ],
    () =>
      client.masterTemplateReports.getUserResults(
        accountId as string,
        masterTemplateId as string,
        { take, skip, sort, filters },
      ),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
      enabled: !!accountId && !!masterTemplateId && !!enabled,
    },
  );

  return {
    masterTemplateUserResults: data,
    ...(rest as UseQueryResult<ListMasterTemplateUserResultsResponse>),
  };
};
